import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Search, Library, Loader2 } from 'lucide-react';
import { TYPE_COLORS, DATA_TYPE_OPTIONS, type FieldLibraryItem } from '@/lib/admin/field-utils';

interface FieldPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (fields: FieldLibraryItem[]) => void;
  excludeFieldIds: string[];
}

export function FieldPicker({ open, onOpenChange, onSelect, excludeFieldIds }: FieldPickerProps) {
  const [fields, setFields] = useState<FieldLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showCommonOnly, setShowCommonOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      fetchFields();
      setSelectedIds(new Set());
      setSearchQuery('');
    }
  }, [open]);

  const fetchFields = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('field_library')
      .select('*')
      .order('is_common', { ascending: false })
      .order('display_order');

    if (!error && data) {
      setFields(data as FieldLibraryItem[]);
    }
    setLoading(false);
  };

  const availableFields = useMemo(() => {
    const excludeSet = new Set(excludeFieldIds);
    return fields.filter((f) => !excludeSet.has(f.id));
  }, [fields, excludeFieldIds]);

  const filteredFields = useMemo(() => {
    return availableFields.filter((field) => {
      const matchesSearch =
        !searchQuery ||
        field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = typeFilter === 'all' || field.data_type === typeFilter;
      const matchesCommon = !showCommonOnly || field.is_common;

      return matchesSearch && matchesType && matchesCommon;
    });
  }, [availableFields, searchQuery, typeFilter, showCommonOnly]);

  const commonFields = useMemo(() => filteredFields.filter((f) => f.is_common), [filteredFields]);
  const otherFields = useMemo(() => filteredFields.filter((f) => !f.is_common), [filteredFields]);

  const toggleSelection = (fieldId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(fieldId)) {
      newSelected.delete(fieldId);
    } else {
      newSelected.add(fieldId);
    }
    setSelectedIds(newSelected);
  };

  const handleAddSelected = () => {
    const selectedFields = fields.filter((f) => selectedIds.has(f.id));
    onSelect(selectedFields);
    onOpenChange(false);
  };

  const FieldRow = ({ field }: { field: FieldLibraryItem }) => (
    <div
      className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer rounded-md"
      onClick={() => toggleSelection(field.id)}
    >
      <Checkbox checked={selectedIds.has(field.id)} onCheckedChange={() => toggleSelection(field.id)} />
      <Badge className={TYPE_COLORS[field.data_type] || ''} variant="secondary">
        {field.data_type}
      </Badge>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{field.name}</span>
          {field.format && (
            <Badge variant="outline" className="text-xs">
              {field.format}
            </Badge>
          )}
        </div>
        {field.description && (
          <p className="text-xs text-muted-foreground line-clamp-1">{field.description}</p>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Library className="h-5 w-5" />
            Add Fields from Library
          </DialogTitle>
          <DialogDescription>
            Select fields to add to this data standard. Fields already added are hidden.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search fields..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {DATA_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch id="common-picker" checked={showCommonOnly} onCheckedChange={setShowCommonOnly} />
              <Label htmlFor="common-picker" className="text-sm cursor-pointer">
                Common only
              </Label>
            </div>
          </div>

          {/* Field List */}
          <ScrollArea className="h-[400px] border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredFields.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <Library className="h-8 w-8 mb-2" />
                <p className="text-sm">No fields available</p>
              </div>
            ) : (
              <div className="p-2 space-y-4">
                {commonFields.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-3 mb-2">
                      Common Fields
                    </h4>
                    <div className="space-y-1">
                      {commonFields.map((field) => (
                        <FieldRow key={field.id} field={field} />
                      ))}
                    </div>
                  </div>
                )}
                {otherFields.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-3 mb-2">
                      {commonFields.length > 0 ? 'Other Fields' : 'All Fields'}
                    </h4>
                    <div className="space-y-1">
                      {otherFields.map((field) => (
                        <FieldRow key={field.id} field={field} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddSelected} disabled={selectedIds.size === 0}>
            Add {selectedIds.size > 0 ? `${selectedIds.size} Selected` : 'Selected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
